import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingDown, TrendingUp, Users } from 'lucide-react';

const STATS = [
  { icon: Users, target: 70, decimals: 0, prefix: '', suffix: '+', label: 'Organisations supported', sub: 'across health, care & purpose-led sectors' },
  { icon: TrendingDown, target: 3.5, decimals: 2, prefix: '£', suffix: '', label: 'Cost per lead, Kaylo', sub: 'down from £25 through paid ads' },
  { icon: TrendingDown, target: 25, decimals: 0, prefix: '£', suffix: '', label: 'Cost per registration, Reset Connect', sub: 'down from £40' },
  { icon: TrendingUp, target: 50, decimals: 0, prefix: '+', suffix: '%', label: 'YoY sales, The Name Shops', sub: 'record Christmas 2025' },
];

const CountUp = ({ target, decimals, prefix, suffix, run }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target]);
  return <span>{prefix}{val.toFixed(decimals)}{suffix}</span>;
};

const ResultsWall = ({ theme = 'dark' }) => {
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setRun(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const dark = theme === 'dark';
  return (
    <section ref={ref} data-testid="results-wall" style={{ background: dark ? 'var(--dark)' : 'var(--off-white)', padding: '80px 0' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', lineHeight: 1.2, display: 'inline-block' }}>
            <span className="heading-block-pink">REAL RESULTS, REAL ORGANISATIONS</span>
          </h2>
          <p style={{ fontSize: 18, color: dark ? 'rgba(255,255,255,0.85)' : 'var(--body-text)', maxWidth: 640, margin: '20px auto 0', lineHeight: 1.7 }}>
            We measure our work by the outcomes we create, lower costs, more enquiries and sustainable growth for health and care organisations.
          </p>
        </div>

        <div data-testid="results-wall-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 24 }}>
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} data-testid={`results-stat-${i}`} style={{
                background: dark ? 'rgba(255,255,255,0.06)' : '#fff',
                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#e6e6e6'}`,
                borderRadius: 14, padding: '32px 24px', textAlign: 'center',
              }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
                  <Icon size={24} color="#fff" />
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: dark ? '#fff' : 'var(--dark)', lineHeight: 1 }}>
                  <CountUp {...s} run={run} />
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--teal)', margin: '14px 0 6px' }}>{s.label}</div>
                <div style={{ fontSize: 18, color: dark ? 'rgba(255,255,255,0.7)' : 'var(--body-text)', lineHeight: 1.5 }}>{s.sub}</div>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: 44, display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/quiz" className="btn-pink-filled" data-testid="results-wall-quote-cta">
            GET MY FREE PLAN &amp; QUOTE <ArrowRight size={14} />
          </Link>
          <Link to="/digital-marketing-case-studies" data-testid="results-wall-cases-cta" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px',
            border: `2px solid ${dark ? 'rgba(255,255,255,0.4)' : 'var(--dark)'}`, color: dark ? '#fff' : 'var(--dark)',
            fontFamily: 'var(--font-heading)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            SEE CASE STUDIES
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ResultsWall;
